import { cn } from "@/lib/utils";
import { Button } from "@/modules/shadcn/ui/button";
import { Loader2, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Asks Robopipe Vision for a description of a label and resolves to the text
 * (never saved). `labelId` is absent while the label is still being created.
 * Rejects with the RTK error so each caller can show it where it fits.
 */
export type GenerateLabelDescription = (input: {
  labelId?: number;
  name: string;
  currentDescription: string;
}) => Promise<string>;

interface GenerateDescriptionButtonProps {
  pending: boolean;
  /** Why it can't run (e.g. no name yet); shown as the tooltip and disables it. */
  disabledReason?: string | null;
  onClick: () => void;
  /** Icon only, for tight rows; the tooltip carries the words. */
  iconOnly?: boolean;
  className?: string;
}

/** The ✨ that writes a label description. Same icons as the Auto-annotate tool. */
export const GenerateDescriptionButton = ({
  pending,
  disabledReason,
  onClick,
  iconOnly,
  className,
}: GenerateDescriptionButtonProps) => {
  const { t } = useTranslation(["label", "common"]);
  const icon = pending ? (
    <Loader2 className="size-4 animate-spin" />
  ) : (
    <Sparkles className="size-4" />
  );
  const title =
    disabledReason ??
    (pending ? t("generateDescription.writing") : t("generateDescription.title"));
  return (
    <Button
      type="button"
      variant="ghost"
      size={iconOnly ? "icon" : "xs"}
      title={title}
      aria-label={iconOnly ? t("generateDescription.aria") : undefined}
      disabled={pending || !!disabledReason}
      onClick={onClick}
      className={cn("shrink-0 text-foreground/70", className)}
    >
      {icon}
      {!iconOnly &&
        (pending ? t("generateDescription.generating") : t("generateDescription.generate"))}
    </Button>
  );
};
